import React, { useEffect, useRef } from "react";
import { prepareAudiosToManifest } from "../../utils/PageManifestParsing/audioParsing";
import { TimelineDrawer } from "../TimelineDrawer";
import { isTimelineEnabled } from "../../utils/Lesson";
import { useLessonPage, useLessonPagesState } from "../../contexts/LessonPagesProvider/LessonPagesProvider";
import { useAudioManagerStore } from "../../contexts/PageAudioManager";
import { useObjectsUtils } from "../../contexts/ObjectsProvider";
import { useNarrationStore } from "../../panels/TimelinePanel/Right/CompositeNarrationRow/store";
import { useShallow } from "zustand/react/shallow";
import { EffectAudio, NarrationAudio } from "../../models/IInteractiveAudio";
import { audioBlobToApi } from "../../panels/TimelinePanel/helpers";
import { getSortedAudios } from "../../contexts/PageAudioManager/helpers";
import { useAudioEffectStore } from "../../panels/TimelinePanel/Right/AudioEffectRow/store";

export const DrawerContainer = () => {
  const lessonPagesState = useLessonPagesState();
  const [loadNarrationAudio, loadEffectAudio, resetNarration, resetEffect, isDirty, setIsDirty] = useAudioManagerStore(
    useShallow((state) => [
      state.loadNarrationAudio,
      state.loadEffectAudio,
      state.resetNarration,
      state.resetEffects,
      state.isDirty,
      state.setIsDirty,
    ]),
  );
  const narrationAudios = useAudioManagerStore((state) => state.narrationAudios);
  const audioEffects = useAudioManagerStore((state) => state.audioEffects);
  const { pageManifest, updateManifest, currentLessonPage, currentLessonPageIndex, currentAggregationOrder } =
    useLessonPage();
  const { needtoSave } = useObjectsUtils();
  const { setObjects } = useNarrationStore();
  const { setObjects: setAudioEffects } = useAudioEffectStore();

  const hasTimeline = isTimelineEnabled(lessonPagesState.currentLessonPage);

  // Composite identity of the active page. Must include aggregation order so that
  // switching between a microlesson page and a base page (where pageId/index may
  // collide) reliably re-runs the load effect.
  const pageKey = `${currentAggregationOrder ?? ""}:${currentLessonPageIndex ?? ""}:${currentLessonPage?.pageId ?? ""}`;
  const loadedPageKey = useRef<string | null>(null);

  // Load audio from manifest when page changes
  useEffect(() => {
    loadedPageKey.current = null;

    resetNarration();
    resetEffect();

    const audiosNarrationToLoad = getSortedAudios([...(pageManifest?.narrationAudios ?? [])]) as NarrationAudio[];

    for (const audio of audiosNarrationToLoad) {
      loadNarrationAudio(audio.objectId, audioBlobToApi(audio.input as string), {
        ...audio,
      });
    }

    const audiosEffectsToLoad = getSortedAudios([...(pageManifest?.audioEffects ?? [])]) as EffectAudio[];

    for (const audio of audiosEffectsToLoad) {
      loadEffectAudio(audio.objectId, audioBlobToApi(audio.input as string), {
        ...audio,
      });
    }

    const rafId = requestAnimationFrame(() => {
      loadedPageKey.current = pageKey;
    });
    return () => cancelAnimationFrame(rafId);
  }, [pageKey]);

  // Save
  useEffect(() => {
    if (isDirty) {
      needtoSave();
      setIsDirty(false);
    }
  }, [isDirty]);

  // Sync narration audio to manifest. Only write back when the store reflects
  // the page currently mounted — prevents late async loads from a previous
  // page leaking their audios into a different page's manifest.
  useEffect(() => {
    setObjects(narrationAudios);

    if (loadedPageKey.current !== pageKey) return;

    updateManifest({
      audios: [],
      narrationAudios: prepareAudiosToManifest(narrationAudios),
    });
  }, [narrationAudios, pageKey]);

  // Sync audio effects to manifest
  useEffect(() => {
    setAudioEffects(audioEffects);

    if (loadedPageKey.current !== pageKey) return;

    updateManifest({
      audioEffects: prepareAudiosToManifest(audioEffects),
    });
  }, [audioEffects, pageKey]);

  return <>{hasTimeline && <TimelineDrawer />}</>;
};
